/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Clear, PriorityHigh } from '@mui/icons-material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getBookedSendingMissions, getSendingMissionDates, saveBookedSendingMissions } from 'src/shared/services/sndMissionService';
import {
  BookedSendingMission,
  invoicesCreatedFromStatus,
  cancelledFromStatus,
  invoicesSentFromStatus,
  MissionStatusEnum,
  missionTypeOf,
  SendingMissionDate,
  statusFromChecks,
} from 'src/modules/support/models/bookedSendingMission';

// Ändringar som ännu inte sparats, nycklade på uppdragets id.
interface PendingEdit {
  missionStatus: string;
  supportComment: string;
}

const fmtDate = (value: string | undefined): string => (value ? dayjs(value).format('ddd D MMM YYYY') : '');

/**
 * Supportens lista över bokade utskicksuppdrag (snd_missions). Samma innehåll som 1.0-sidan sndMissions.asp –
 * ett produktionsdatum i taget – men kollegorna bockar av fakturornas gång direkt i listan och sparar allt med
 * knappen i footern. Kryssrutorna speglar uppdragets missionStatus.
 */
export const BookedSendingMissionsPage = () => {
  const isMounted = useIsMounted();

  const [dates, setDates] = useState<SendingMissionDate[]>([]);
  const [dateId, setDateId] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<BookedSendingMission[]>([]);
  const [edits, setEdits] = useState<Record<number, PendingEdit>>({});
  const [search, setSearch] = useState('');
  const [hideDone, setHideDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Datumen hämtas en gång; närmast kommande (eller senaste passerade) väljs som startvy.
  useEffect(() => {
    (async () => {
      try {
        const res = await getSendingMissionDates();
        if (!isMounted()) return;

        const list = res.data ?? [];
        setDates(list);

        const today = dayjs().startOf('day');
        const upcoming = [...list].reverse().find((d) => !dayjs(d.sndDate).isBefore(today, 'day'));

        setDateId((upcoming ?? list[0])?.sndDate_id);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getSendingMissionDates', e);
      }
    })();
  }, [isMounted]);

  const load = useCallback(async () => {
    if (!dateId) return;

    setLoading(true);
    try {
      const res = await getBookedSendingMissions(dateId);
      if (!isMounted()) return;
      setRows(res.data ?? []);
      setEdits({});
    } catch (e) {
      if (isMounted()) ConsoleHelper.log('error getBookedSendingMissions', e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [dateId, isMounted]);

  useEffect(() => {
    load();
  }, [load]);

  // Aktuellt värde för en rad: den osparade ändringen om den finns, annars serverns.
  const valueOf = useCallback(
    (row: BookedSendingMission): PendingEdit =>
      edits[row.sndMission_id] ?? { missionStatus: row.missionStatus, supportComment: row.supportComment ?? '' },
    [edits],
  );

  const setEdit = useCallback((row: BookedSendingMission, next: Partial<PendingEdit>) => {
    setEdits((prev) => {
      const current = prev[row.sndMission_id] ?? { missionStatus: row.missionStatus, supportComment: row.supportComment ?? '' };
      const merged = { ...current, ...next };

      // Tillbaka till serverns värden → raden är inte längre ändrad.
      if (merged.missionStatus === row.missionStatus && merged.supportComment === (row.supportComment ?? '')) {
        const rest = { ...prev };
        delete rest[row.sndMission_id];
        return rest;
      }

      return { ...prev, [row.sndMission_id]: merged };
    });
  }, []);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const status = valueOf(row).missionStatus;

      if (hideDone && (status === MissionStatusEnum.Slutford || status === MissionStatusEnum.Uppskjuten)) return false;
      if (!term) return true;

      return `${row.customerName} ${missionTypeOf(row)} ${row.info}`.toLowerCase().includes(term);
    });
  }, [rows, search, hideDone, valueOf]);

  const changedCount = Object.keys(edits).length;

  const handleSave = useCallback(async () => {
    if (changedCount === 0) return;

    setSaving(true);
    try {
      const items = Object.entries(edits).map(([id, edit]) => ({
        sndMission_id: Number(id),
        missionStatus: edit.missionStatus,
        supportComment: edit.supportComment,
      }));

      await saveBookedSendingMissions(items);
      if (!isMounted()) return;

      // Spegla det sparade direkt i tabellen; omhämtningen nedan bekräftar sedan mot servern.
      const savedById = new Map(items.map((item) => [item.sndMission_id, item]));

      setRows((prev) =>
        prev.map((row) => {
          const saved = savedById.get(row.sndMission_id);
          return saved ? { ...row, missionStatus: saved.missionStatus, supportComment: saved.supportComment } : row;
        }),
      );
      setEdits({});

      toast.success(`${items.length} ${items.length === 1 ? 'uppdrag sparat' : 'uppdrag sparade'}.`);
      await load();
    } catch (e) {
      ConsoleHelper.log('error saveBookedSendingMissions', e);
      toast.error('Uppdragen kunde inte sparas.');
    } finally {
      if (isMounted()) setSaving(false);
    }
  }, [changedCount, edits, isMounted, load]);

  // Varna innan sidan lämnas med osparade ändringar.
  useEffect(() => {
    if (changedCount === 0) return undefined;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [changedCount]);

  const currentIndex = dates.findIndex((d) => d.sndDate_id === dateId);

  return (
    <GridWrapper>
      <Grid item xs={12} md={6}>
        <MainContentHeader title='Bokade utskicksuppdrag' />
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Datumen listas nyast först, så "föregående datum" ligger nedåt i listan. */}
          <Tooltip title='Föregående datum'>
            <span>
              <IconButton
                size='small'
                disabled={currentIndex < 0 || currentIndex >= dates.length - 1}
                onClick={() => setDateId(dates[currentIndex + 1]?.sndDate_id)}
              >
                <ArrowBackIos fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            select
            size='small'
            label='Utskicksdatum'
            value={dateId ?? ''}
            onChange={(e) => setDateId(Number(e.target.value))}
            sx={{ minWidth: 240 }}
          >
            {dates.map((d) => (
              <MenuItem key={d.sndDate_id} value={d.sndDate_id}>
                {fmtDate(d.sndDate)}
              </MenuItem>
            ))}
          </TextField>

          <Tooltip title='Nästa datum'>
            <span>
              <IconButton size='small' disabled={currentIndex <= 0} onClick={() => setDateId(dates[currentIndex - 1]?.sndDate_id)}>
                <ArrowForwardIos fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>

          <TextField size='small' label='Sök kund eller uppdrag' value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />

          <FormControlLabel control={<Switch checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />} label='Dölj klara och avbrutna' />

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant='body2' color='text.secondary'>
            {loading ? 'Hämtar …' : `${visibleRows.length} uppdrag`}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Kund och uppdrag</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='center'>Fakturor skapade</TableCell>
                <TableCell align='center'>Fakturor skickade</TableCell>
                <TableCell align='center'>Avbruten</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Kommentar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant='body2' color='text.secondary'>
                      Inga uppdrag på det här datumet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                visibleRows.map((row) => {
                  const edit = valueOf(row);
                  const created = invoicesCreatedFromStatus(edit.missionStatus);
                  const sent = invoicesSentFromStatus(edit.missionStatus);
                  const cancelled = cancelledFromStatus(edit.missionStatus);
                  const dirty = !!edits[row.sndMission_id];

                  return (
                    <TableRow
                      key={row.sndMission_id}
                      // Avbrutna uppdrag tonas ned så listan går att skumma efter det som återstår.
                      sx={{ bgcolor: dirty ? 'action.hover' : undefined, opacity: cancelled ? 0.55 : 1 }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            {row.customerName}
                          </Typography>
                          {row.deviant === 1 && (
                            <Tooltip title='Avvikande uppdrag'>
                              <PriorityHigh color='error' fontSize='small' />
                            </Tooltip>
                          )}
                          {row.status !== 'bokad' && <Chip size='small' label='Preliminär' color='warning' variant='outlined' />}
                        </Box>

                        {/* Uppdraget står under kundnamnet med info-texten inom parentes – samma uppställning som 1.0. */}
                        <Typography variant='body2' color='text.secondary'>
                          {missionTypeOf(row)}
                          {row.info ? ` (${row.info})` : ''}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip size='small' label={edit.missionStatus} variant='outlined' />
                      </TableCell>

                      <TableCell align='center'>
                        <Checkbox
                          checked={created}
                          disabled={cancelled}
                          // Kryssrutorna sätter statusen: skapade = Pågår, skapade + skickade = Slutförd.
                          onChange={(e) => setEdit(row, { missionStatus: statusFromChecks(e.target.checked, e.target.checked && sent, false) })}
                        />
                      </TableCell>

                      <TableCell align='center'>
                        <Checkbox
                          checked={sent}
                          disabled={cancelled}
                          // Skickade förutsätter skapade – därför kryssas båda.
                          onChange={(e) => setEdit(row, { missionStatus: statusFromChecks(e.target.checked || created, e.target.checked, false) })}
                        />
                      </TableCell>

                      <TableCell align='center'>
                        <Tooltip title='Uppdraget ska inte produceras. Sätter status Uppskjuten.'>
                          <Checkbox
                            checked={cancelled}
                            color='error'
                            // Avbruten går före de andra: kryssas den nollställs fakturastegen.
                            onChange={(e) => setEdit(row, { missionStatus: statusFromChecks(false, false, e.target.checked) })}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          placeholder='Anteckning'
                          value={edit.supportComment}
                          onChange={(e) => setEdit(row, { supportComment: e.target.value })}
                          helperText={row.supportUpdatedBy ? `Senast sparad av ${row.supportUpdatedBy}` : undefined}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Samma fasta footer som medlemsregistret visar när rader markerats – ligger kvar över innehållet. */}
      {changedCount > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => setEdits({})} sx={{ pr: 1 }} title='Ångra ändringarna'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {changedCount} {changedCount === 1 ? 'uppdrag ändrat' : 'uppdrag ändrade'}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Button variant='contained' disableElevation disabled={saving} onClick={handleSave}>
              Spara
            </Button>
          </Box>
        </FixedFooter>
      )}
    </GridWrapper>
  );
};
